import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box, Button, Checkbox, Divider, Popover, PopoverArrow, PopoverBody, PopoverCloseButton, PopoverContent, PopoverHeader, PopoverTrigger, VStack } from '@chakra-ui/react';
import { useState } from 'react';
import { translate } from '../../../i18n';
import { SportType } from '../../../models/court/dto/court.dto';

type Props = {
    sports: SportType[];
    selectedSports: SportType[];
    onChange: (sports: SportType[]) => void;
};

export default function MultiSportPicker({ sports, selectedSports, onChange }: Props) {
    const [isOpen, setIsOpen] = useState(false);

    const handleToggle = (sport: SportType) => {
        const next = selectedSports.includes(sport)
            ? selectedSports.filter(s => s !== sport)
            : [...selectedSports, sport];
        onChange(next);
    };

    const allSelected = sports.length > 0 && selectedSports.length === sports.length;
    const someSelected = selectedSports.length > 0 && !allSelected;

    const label = selectedSports.length === 0
        ? translate('analytics.filters.all_sports')
        : selectedSports.length > 3
            ? `${selectedSports.length} ${translate('analytics.filters.sports')}`
            : selectedSports.join(', ');

    return (
        <Popover isOpen={isOpen} onClose={() => setIsOpen(false)}>
            <PopoverTrigger>
                <Button
                    onClick={() => setIsOpen(true)}
                    variant='outline'
                    size='sm'
                    rightIcon={<ChevronDownIcon />}
                    fontWeight='600'
                    fontSize='sm'
                    width='180px'
                    justifyContent='space-between'
                >
                    <Box as='span' overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap'>
                        {label}
                    </Box>
                </Button>
            </PopoverTrigger>
            <PopoverContent>
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverHeader fontWeight='600'>{translate('analytics.filters.sports')}</PopoverHeader>
                <PopoverBody maxH='300px' overflowY='auto'>
                    <VStack align='start'>
                        <Checkbox
                            isChecked={allSelected}
                            isIndeterminate={someSelected}
                            isDisabled={sports.length === 0}
                            onChange={() => onChange(allSelected ? [] : [...sports])}
                            fontWeight='600'
                        >
                            {allSelected
                                ? translate('analytics.filters.deselect_all')
                                : translate('analytics.filters.select_all')}
                        </Checkbox>
                        <Divider />
                        {sports.map(sport => (
                            <Checkbox
                                key={sport}
                                isChecked={selectedSports.includes(sport)}
                                onChange={() => handleToggle(sport)}
                            >
                                {sport}
                            </Checkbox>
                        ))}
                    </VStack>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}
